import JSZip from 'jszip';
import { strToU8 } from 'fflate';
import { AssetContainer } from '@babylonjs/core/assetContainer';
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import type { ISceneLoaderProgressEvent, SceneLoaderPluginOptions } from '@babylonjs/core/Loading/sceneLoader';
import { GLTFLoaderAnimationStartMode } from '@babylonjs/loaders/glTF/glTFFileLoader.pure';
import type { Scene } from '@babylonjs/core/scene';
import { Tools } from '@babylonjs/core/Misc/tools';
import { RetryStrategy } from '@babylonjs/core/Misc/retryStrategy';
import { Logger } from '../utils/Logger';
import { DownloadActivity, withDownloadStall, type DownloadProgress } from './DownloadStall';
import { Utility } from '../utils/Utility';
import { isLocalAssetPath } from '../utils/assetPaths';
import { isProvenanceHistoryField } from '../export/behaviorAssetFields';
import { SceneInfo } from '../scenedata/sceneinfo';
import { TraceSceneInfoFile } from '../scenedata/infofiledata';
import type { ZipEntry, ZipProgressCallback } from '../export/zip/zipWriter';

/**
 * Babylon's per-request progress event, as the bytes-arrived signal
 * `withDownloadStall` listens for. Nothing when nobody is listening, so a load
 * that is not being watched passes Babylon no callback.
 */
function progressOption(progress: DownloadProgress | undefined): { onProgress?: (event: ISceneLoaderProgressEvent) => void } {
    if (!progress) return {};
    return { onProgress: event => progress(event.loaded, event.lengthComputable ? event.total : undefined) };
}

// Disable Babylon.js file-loading retries to fail faster
Tools.DefaultRetryStrategy = RetryStrategy.ExponentialBackoff(0, 0);

// 2024
// Author: Aaron Post

/**
 * Resolve an asset reference (a SceneObject `src` or a script path) through a
 * `remap` produced by {@link ResourceManager.mergeSceneAssets}.
 *
 * `remap` keys are bare asset paths (`assets/panel.glb`), but a `src` may carry
 * a `./` prefix (`./assets/panel.glb`). We match on the normalized (prefix-
 * stripped) form and re-apply the prefix, so `./assets/panel.glb` →
 * `./assets/F40Slide1/panel.glb`. References not in the remap (HTTP URLs, nested
 * scene refs, untouched assets) pass through unchanged.
 */
export function remapAssetRef(ref: string | undefined, remap: Map<string, string>): string | undefined {
    if (!ref) return ref;
    const hadPrefix = ref.startsWith('./');
    const normalized = hadPrefix ? ref.slice(2) : ref;
    const mapped = remap.get(normalized);
    if (!mapped) return ref;
    return hadPrefix ? `./${mapped}` : mapped;
}

/**
 * Recursively rewrite every local-asset-path string inside a behavior-data tree
 * through `remap`, in place. Behaviors reference their own subassets by path in
 * arbitrary fields — `Renderer.materials[].src`, `Frame.src`, `ImageAnchor.src`,
 * `Animation.src`, `AnimationSequence.states.{enter,main,exit}.src`, … — and
 * those are read straight off authored data at load time (e.g.
 * `getOrLoadContainer(materialData.src)`). Because {@link mergeSceneAssets}
 * *moves* a nested scene's blob to a scene-scoped key (no copy left behind),
 * these embedded paths must be rewritten too, or the behavior resolves a key
 * that no longer exists. Like TraceExport.extractBehaviorAssets, inert generation
 * history is carried without remapping.
 */
export function remapBehaviorAssetRefs(behaviors: unknown, remap: Map<string, string>): void {
    if (remap.size === 0) return;

    const walk = (value: unknown): void => {
        if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
                const item: unknown = value[i];
                if (isLocalAssetPath(item)) {
                    value[i] = remapAssetRef(item, remap);
                } else {
                    walk(item);
                }
            }
        } else if (value && typeof value === 'object') {
            const obj = value as Record<string, unknown>;
            for (const key of Object.keys(obj)) {
                if (isProvenanceHistoryField(obj, key)) continue;
                const item = obj[key];
                if (isLocalAssetPath(item)) {
                    obj[key] = remapAssetRef(item, remap);
                } else {
                    walk(item);
                }
            }
        }
    };

    walk(behaviors);
}

/**
 * Rewrite, through `remap`, the asset paths inside each style sheet a
 * `Properties.sheets` entry of `nodes` names. A sheet names its material files
 * in its own bytes, which {@link remapBehaviorAssetRefs} never sees, so when
 * {@link ResourceManager.mergeSceneAssets} moves a sheet and its files they
 * would otherwise resolve nowhere. Run it after `TraceScene._applyAssetKeyRemap`,
 * which points the `sheets` entries at the moved keys. Only sheets this move
 * re-homed are rewritten: a key the scene already held is not this import's.
 */
export async function remapSheetAssetRefs(
    nodes: readonly { behaviors?: readonly unknown[] }[],
    resources: ResourceManager,
    remap: Map<string, string>,
): Promise<void> {
    const moved = new Set(remap.values());
    const sheetKeys = new Set<string>();
    for (const node of nodes) {
        for (const behavior of node.behaviors ?? []) {
            const { '@type': type, sheets } = behavior as { '@type'?: string; sheets?: unknown };
            if (type !== 'Properties' || !Array.isArray(sheets)) continue;
            for (const path of sheets) {
                const key = isLocalAssetPath(path) ? path.replace(/^\.\//, '') : undefined;
                if (key && moved.has(key)) sheetKeys.add(key);
            }
        }
    }
    for (const key of sheetKeys) {
        const blob = resources.getAsset(key);
        if (!blob) continue;
        let sheet: unknown;
        try {
            sheet = JSON.parse(await blob.text());
        } catch {
            // Left as it is: its paths can't be read, so they can't be moved.
            Logger.warn(`[ResourceManager] Sheet ${key} is not JSON; the asset paths inside it were not remapped`);
            continue;
        }
        const before = JSON.stringify(sheet);
        remapBehaviorAssetRefs(sheet, remap);
        const after = JSON.stringify(sheet);
        if (after !== before) resources.addAsset(key, new Blob([after], { type: blob.type }));
    }
}

/**
 * Options for {@link ResourceManager.getOrLoadContainer}. The same value must
 * be passed to {@link ResourceManager.releaseContainer}: the option is part of
 * the container cache key, so a model load and a material load of one URL
 * never share a container.
 */
export interface ContainerLoadOptions {
    /**
     * Instantiate every material in a glTF, not only those a mesh primitive
     * references (Babylon's `loadAllMaterials`). Material assets need it: a
     * geometry-less `.gltf` has no primitive, so without it
     * `container.materials` is empty. Keep it off for model loads — it would
     * instantiate every unused material in the file.
     */
    allMaterials?: boolean;
}

/** The subset of Babylon's glTF plugin options this manager sets. */
type GltfPluginOptions = Pick<NonNullable<SceneLoaderPluginOptions['gltf']>, 'loadAllMaterials' | 'preprocessUrlAsync' | 'animationStartMode'>;

/**
 * Separates a container cache key's path part from its option suffix. `|` is
 * not a legal URL character (browsers percent-encode it) and never appears in
 * the `assets/…` keys the store hands out.
 */
const VARIANT_SEPARATOR = '|';
/** Cache-key suffix for `allMaterials` loads. */
const ALL_MATERIALS_KEY_SUFFIX = VARIANT_SEPARATOR + 'allMaterials';

export class ResourceManager {
    private assets: Map<string, Blob>;
    /** Scene data in standard format (scene.json/models.json) */
    private json: SceneInfo | undefined;
    /** Optional info.json data from source .zip trace scene (preserved for export) */
    private info: TraceSceneInfoFile | undefined;
    /** Content hash per blob, for deduplication (see computeBlobHash) */
    private readonly blobHashes = new WeakMap<Blob, Promise<string>>();
    /** Blobs added through addAssetWithDedup: the ones it may hand out under another name */
    private readonly dedupBlobs = new WeakSet<Blob>();
    /** Cached AssetContainers keyed by normalized asset path */
    private containerCache: Map<string, Promise<AssetContainer>> = new Map();
    /** Container downloads in flight wait for each other (see DownloadActivity). */
    private readonly downloads = new DownloadActivity();
    /** Reference counts for cached containers */
    private containerRefCount: Map<string, number> = new Map();
    /**
     * The container each load promise resolved to. Keyed by the promise, not
     * the path, so every way an entry leaves `containerCache` (release, a
     * rejected load, a same-path re-upload) also ends its synchronous read.
     */
    private settledContainers: WeakMap<Promise<AssetContainer>, AssetContainer> = new WeakMap();
    /**
     * Containers a same-path re-upload replaced while something still held
     * them. Disposing one under a live holder blanks the on-stage Model, so
     * they leak until clearAssets() — the same deal the inspector's session
     * pin already takes.
     */
    private _orphaned: Promise<AssetContainer>[] = [];
    /** Maps external URLs to local asset keys so HTTP-sourced assets load from cache */
    private urlToAssetKey = new Map<string, string>();
    /** Scene-folder segments already allocated this session (keeps nested-scene folders unique) */
    private usedSceneFolders = new Set<string>();
    /** Maps a nested-scene source key (its URL) to its allocated folder, so repeat
     *  placements of the SAME source reuse one folder — and thus keep sharing the
     *  container cache — instead of minting Foo, Foo-2, Foo-3, … per placement. */
    private sourceToSceneFolder = new Map<string, string>();
    /** Maps an asset key to the directory URL it was fetched from, so external
     *  references inside the asset (a .gltf's .bin/textures) can be resolved
     *  against its source location when no local sibling blob is registered. */
    private assetBaseUrls = new Map<string, string>();

    /**
     * Folder-relative asset key → the absolute URL it resolves to. Populated
     * when a scene document is loaded from a URL; see
     * {@link registerAssetUrl}.
     */
    private assetUrls = new Map<string, string>();

    /**
     * Asset key → the absolute URL the bytes we HOLD were fetched from.
     *
     * The mirror image of {@link assetUrls}, and the distinction is the whole
     * point: `assetUrls` says *the bytes are not here, get them from there*,
     * this says *the bytes are here, and here is where they came from*. See
     * {@link registerAssetSourceUrl}.
     */
    private assetSourceUrls = new Map<string, string>();

    /**
     * Asset key → the file's ORIGINAL human name (`chair.glb`), spec §2.1.
     *
     * Purely informational, and deliberately a SIDE map rather than a field on
     * the asset: storage names files by hash, `SceneObject.src` keeps the hash
     * name, and nothing may ever resolve bytes by this string. It exists so a
     * load → save round trip can put `files[k].name` back where it found it,
     * and so an editor can label a node with what the user dropped.
     *
     * Written from, in order of authority: the `name` echoed by
     * `POST /scenes/{id}/files`, the dropped `File.name` recorded at import,
     * and the `files[k].name` of the document that was loaded.
     */
    private assetNames = new Map<string, string>();

    /**
     * Folder URL → authored uri → the uri that folder stored those bytes
     * under, and their SHA-256. See {@link recordStoredFiles}.
     */
    private storedFolderFiles = new Map<string, Map<string, { uri: string; sha256: string }>>();

    constructor() {
        this.assets = new Map<string, Blob>();
    }

    /**
     * The full SHA-256 of a blob, once per Blob (they are immutable). Whole,
     * not sampled: a match hands back another asset's path, so files that
     * merely agree on a few windows would become one (#631). The same
     * identity the CMS names folder files by (`<slug>.<hash16>.<ext>`).
     */
    private computeBlobHash(blob: Blob): Promise<string> {
        let hash = this.blobHashes.get(blob);
        if (!hash) {
            hash = blob.arrayBuffer()
                .then(buffer => crypto.subtle.digest('SHA-256', buffer))
                .then(digest => Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join(''));
            // A failed read (a revoked file handle) is not cached: the next add retries.
            hash.catch(() => this.blobHashes.delete(blob));
            this.blobHashes.set(blob, hash);
        }
        return hash;
    }

    /**
     * Add an asset with content-based deduplication.
     * If an identical asset already exists, returns the existing path instead.
     *
     * Candidates are assets added here, plus whatever holds `assetName`
     * itself (a reopened `.trace`, a host's folder file). Another name a host
     * put in place with `addAsset` is not handed out: the node would be
     * authored with a key the host owns, not the one it asked for. Only a
     * candidate of the same size can hold the same bytes, so nothing is read
     * or hashed unless one exists: a video import is not buffered whole to
     * find out it is new.
     *
     * Different bytes never take a path another asset holds: they go to the
     * first free `name-2.ext`, `name-3.ext`… beside it. Three photos all named
     * `image.png` are three assets, and overwriting the first would repoint
     * every node already placed on it — invisibly until the save (#631).
     *
     * @param assetName - The desired asset name/path
     * @param blob - The asset blob
     * @returns The actual asset path (may differ if deduplicated or taken)
     */
    public async addAssetWithDedup(assetName: string, blob: Blob): Promise<string> {
        for (const [path, held] of this.assets) {
            if (held.size !== blob.size || (path !== assetName && !this.dedupBlobs.has(held))) continue;
            const same = held === blob
                || await this.computeBlobHash(held) === await this.computeBlobHash(blob);
            // Still there after the hash? A remove or overwrite may have run.
            if (same && this.assets.get(path) === held) {
                Logger.debug(`[ResourceManager] Deduplicated asset: ${assetName} -> ${path}`);
                return path;
            }
        }

        // No await from here to the claim: parallel adds (the export's
        // downloads) must not take the same name.
        const dot = assetName.lastIndexOf('.');
        const [stem, ext] = dot > assetName.lastIndexOf('/')
            ? [assetName.slice(0, dot), assetName.slice(dot)]
            : [assetName, ''];
        let path = assetName;
        for (let n = 2; this.assets.has(path) || this.assetUrls.has(path.replace('./', '')); n++) {
            path = `${stem}-${n}${ext}`;
        }
        this.assets.set(path, blob);
        this.dedupBlobs.add(blob);
        Logger.debug(`[ResourceManager] Added new asset: ${path} (${blob.size} bytes)`);
        return path;
    }

    /**
     * Sets the scene data for export
     * @param json - Scene data in standard SceneInfo format
     * @param info - Optional info.json data (only when exporting .zip trace scenes with preview metadata)
     */
    public setJson(json: SceneInfo, info?: TraceSceneInfoFile): void {
        this.json = json;
        if (info) {
            this.info = info;
        }
    }
    public addAsset(assetName: string, blob: Blob)
    {
        this._evictIfReplaced(assetName, blob);
        this.assets.set(assetName, blob);
    }

    /**
     * A path being overwritten with different bytes must not keep serving the
     * old container: `getOrLoadContainer` is keyed by path, and once anything
     * has pinned that key (the sandbox inspector probes a clip's names on
     * render, and keeps the ref for the session) a re-upload through the
     * asset field or the resources panel was a cache hit — clones came from
     * the old GLB until `clearScene()`. Re-adding the very same Blob keeps the cache.
     */
    private _evictIfReplaced(assetName: string, blob: Blob): void {
        const existing = this.assets.get(assetName);
        if (!existing || existing === blob) return;

        // Drop the cache entry so the next load parses the new bytes, but do
        // NOT dispose what a holder is still rendering from: instances and
        // cloneMeshes hosts share the container's meshes, materials and
        // geometries by reference. Nothing re-materializes a Model afterward
        // (setSrc early-returns on an unchanged authored string), so a dispose
        // here is permanent. Dropping the refcount with it also keeps the
        // first stale release from landing on the key the new bytes reclaim.
        // Every variant of the path, not just the plain one: a material load
        // pins `<key>|allMaterials`, and leaving it behind served the OLD
        // bytes to the next material load forever while a model load of the
        // same path correctly saw the new ones.
        for (const key of this._containerKeyVariants(assetName)) {
            const stale = this.containerCache.get(key)!;
            const held = (this.containerRefCount.get(key) ?? 0) > 0;
            this.containerCache.delete(key);
            this.containerRefCount.delete(key);
            if (held) this._orphaned.push(stale);
            else stale.then(c => c.dispose()).catch(() => {});
        }
    }

    /**
     * Every cached container key for `path`: the plain key plus one per
     * load-option variant (`<key>|allMaterials`, …). Matching on the
     * `|`-separated prefix covers options added later, so an eviction path
     * cannot silently miss a variant it was never taught about — which is
     * exactly how a re-uploaded material kept serving its old container.
     */
    private _containerKeyVariants(path: string): string[] {
        const pathKey = this.containerKey(path);
        const variantPrefix = pathKey + VARIANT_SEPARATOR;
        return [...this.containerCache.keys()].filter(key => key === pathKey || key.startsWith(variantPrefix));
    }

    /**
     * Drop and dispose every cached container for `path`, whichever addressing
     * form pinned it and whichever load-option variant it was loaded under.
     */
    private _evictContainer(path: string): void {
        for (const key of this._containerKeyVariants(path)) {
            const containerPromise = this.containerCache.get(key)!;
            this.containerRefCount.delete(key);
            this.containerCache.delete(key);
            containerPromise.then(c => c.dispose()).catch(() => {});
        }
    }

    /**
     * Merge the subassets of a nested scene-format archive into the shared asset
     * store under a scene-scoped folder, so same-named assets from different
     * scenes don't collide.
     *
     * Two separate `.trace` files can each carry `assets/<name>` entries whose
     * filenames coincide but whose content differs (e.g. F40Slide1.trace and
     * F40Slide2.trace both containing `assets/panel.glb`). Assets are path-keyed,
     * so a plain `addAsset` would let the second scene overwrite — or the
     * container cache serve — the first scene's blob, and the second scene would
     * render the first's asset.
     *
     * We resolve this structurally rather than by content hash: each nested
     * scene's assets are re-homed under `assets/<sceneFolder>/<name>`, where
     * `sceneFolder` is a per-source-unique, sanitized segment derived from the
     * scene label. This is collision-proof by construction (no hashing) and
     * keeps the stored path human-readable. The trade-off is no cross-scene
     * dedup: two *different* scenes referencing identical bytes store two copies.
     *
     * Repeat placements of the SAME source (`sourceKey`) reuse one folder, so
     * the same nested `.trace` instantiated N times (a reusable "prefab") keeps
     * a single set of keys — and thus keeps sharing the container cache — rather
     * than re-homing (and re-parsing) per placement.
     *
     * @param incoming - Map of `assets/<name>` path → blob from the archive
     * @param sceneLabel - A human name for the scene (e.g. its file name), used
     *   to derive the folder segment
     * @param sourceKey - Stable identity for the source (its URL). When the same
     *   key recurs, the existing folder is reused and blobs are not re-stored.
     * @returns Map of original path → scene-scoped path. Callers rewrite matching
     *   `src`/script references so behaviors resolve the re-homed key.
     */
    public mergeSceneAssets(incoming: Map<string, Blob>, sceneLabel: string, sourceKey?: string): Map<string, string> {
        // Repeat placement of the same source → reuse its folder (and its already-
        // stored blobs), so the container cache still shares across placements.
        const existingFolder = sourceKey ? this.sourceToSceneFolder.get(sourceKey) : undefined;
        const sceneFolder = existingFolder ?? this.allocateSceneFolder(sceneLabel);
        if (sourceKey && !existingFolder) {
            this.sourceToSceneFolder.set(sourceKey, sceneFolder);
        }

        const remap = new Map<string, string>();
        for (const [originalPath, blob] of incoming) {
            // Re-home `assets/<name>` under `assets/<sceneFolder>/<name>`.
            const fileName = originalPath.replace(/^assets\//, '');
            const scopedPath = `assets/${sceneFolder}/${fileName}`;
            // On a reused folder the blob is already stored — don't overwrite (a
            // no-op set is harmless, but skipping keeps repeat placements cheap).
            if (!existingFolder) this.assets.set(scopedPath, blob);
            // The human name travels with the key it describes (spec §2.1),
            // or a re-save of the host scene loses it on every nested asset.
            const name = this.assetNames.get(originalPath.replace('./', ''));
            if (name) this.assetNames.set(scopedPath, name);
            remap.set(originalPath, scopedPath);
        }

        Logger.log(`[ResourceManager] ${existingFolder ? 'Reused' : 'Merged'} ${incoming.size} subasset(s) under assets/${sceneFolder}/`);
        return remap;
    }

    /**
     * Derive a filesystem-safe, per-load-unique folder segment from a scene
     * label. Scene names are neither unique nor path-safe, so we sanitize to
     * `[A-Za-z0-9._-]` and disambiguate repeats with a `-2`, `-3`, … suffix.
     *
     * We also avoid any segment that already exists as a real asset subfolder
     * (`assets/<candidate>/…`) — e.g. a top-level scene's own `assets/textures/`
     * vs. a nested scene named `textures.trace` — so re-homing can't land on
     * top of pre-existing keys.
     */
    private allocateSceneFolder(label: string): string {
        // Strip any extension and sanitize to a path-safe segment.
        const base = label.replace(/\.[^/.]+$/, '').replace(/[^A-Za-z0-9._-]+/g, '_');
        const safe = base.length > 0 ? base : 'scene';

        let candidate = safe;
        let n = 2;
        while (this.usedSceneFolders.has(candidate) || this.assetSubfolderExists(candidate)) {
            candidate = `${safe}-${n++}`;
        }
        this.usedSceneFolders.add(candidate);
        return candidate;
    }

    /** Whether any stored asset lives under `assets/<folder>/…`. */
    private assetSubfolderExists(folder: string): boolean {
        const prefix = `assets/${folder}/`;
        for (const key of this.assets.keys()) {
            if (key.startsWith(prefix)) return true;
        }
        return false;
    }

    /**
     * Register a mapping from an external URL to a local asset key.
     * Allows _loadContainer to serve HTTP-sourced assets from the local blob cache.
     */
    public registerUrlMapping(url: string, assetKey: string): void {
        this.urlToAssetKey.set(url, assetKey);
    }

    /**
     * Resolve an external URL to its local asset key, if one was registered.
     */
    public resolveUrlToAssetKey(url: string): string | undefined {
        return this.urlToAssetKey.get(url);
    }

    /**
     * Register the absolute URL a folder-relative asset reference resolves to.
     *
     * A folder document names its files relatively
     * (`assets/<slug>.<hash16>.glb`, spec §2.3 — an older row spells the same
     * thing `assets/<sha256>.glb`, and nothing here reads either) and
     * `SceneObject.src` keeps that authored form so a copy of
     * the folder needs no rewrite. The bytes still have to come from
     * somewhere: this records where, resolved against the document's own URL
     * (after any redirect). Only consulted when nothing local answers, so a
     * bundled `.tracex` blob or a sibling file always wins.
     */
    public registerAssetUrl(assetKey: string, url: string): void {
        if (!assetKey || !url) return;
        this.assetUrls.set(assetKey.replace('./', ''), url);
    }

    /**
     * The absolute URL a folder-relative reference resolves to, or undefined
     * when the reference is already absolute or belongs to no known folder.
     */
    public resolveAssetUrl(pathOrUrl: string): string | undefined {
        if (!pathOrUrl || !this.isLocal(pathOrUrl)) return undefined;
        return this.assetUrls.get(pathOrUrl.replace('./', ''));
    }

    /**
     * Record the absolute URL a blob this manager HOLDS was fetched from.
     *
     * Three maps now say something about where an asset lives, and they are
     * not interchangeable:
     *
     *  - {@link registerAssetUrl} — the bytes are NOT here; fetch them from
     *    there. That is a folder scene's files, named by a document and never
     *    downloaded.
     *  - {@link registerAssetBaseUrl} — a DIRECTORY, so a text `.gltf` we do
     *    hold can resolve its `.bin` and textures against where it came from.
     *  - this one — the FILE, for a blob we do hold.
     *
     * The last is what lets a save back into that same folder NAME the file
     * instead of re-uploading it (spec §2.2). An editor that opens a leaf
     * asset row downloads its bytes in order to show them, and without this
     * the export cannot tell those bytes from any other blob in the table:
     * wrap-on-first-edit then re-uploads the very file it is wrapping.
     *
     * Recorded from the RESOLVED response URL, never derived from a request
     * URL or reassembled from parts — a library reference redirects, and the
     * folder that answers is the only one that counts.
     */
    public registerAssetSourceUrl(assetKey: string, url: string): void {
        if (!assetKey || !url) return;
        this.assetSourceUrls.set(assetKey.replace('./', ''), url);
    }

    /** Where a locally-held asset's bytes were fetched from, if recorded. */
    public getAssetSourceUrl(pathOrKey: string): string | undefined {
        if (!pathOrKey) return undefined;
        return this.assetSourceUrls.get(pathOrKey.replace('./', ''));
    }

    /**
     * Record files a save uploaded into `folderUrl` (CONTENT_MODEL_SPEC §2.2):
     * authored uri → the uri the folder stored it under, and the bytes sent.
     *
     * The session holds every imported, regenerated or baked file as a blob,
     * so each export hands the same bytes back to upload. After this, an
     * export into the same folder (`exportFolder({ folderUrl })`) that writes
     * byte-identical content under that authored uri names the stored file
     * instead: it is on `existing`, and the document names the stored uri.
     * Different bytes under the same uri upload as usual.
     *
     * Bound by bytes, so a record cannot go stale through an edit. It can go
     * stale if the folder loses the file; a host whose document write is then
     * refused calls {@link forgetStoredFiles} and saves again in full.
     * Nothing is recorded where SHA-256 is unavailable.
     *
     * Each stored uri is also registered as a file of that folder
     * ({@link registerAssetUrl}), so a document naming it resolves there.
     */
    public async recordStoredFiles(
        folderUrl: string,
        files: ReadonlyMap<string, { uri: string; blob: Blob }>,
    ): Promise<void> {
        const hashed = await Promise.all([...files].map(async ([authored, stored]) =>
            [authored, stored.uri, await sha256Hex(stored.blob)] as const));
        let known = this.storedFolderFiles.get(folderUrl);
        if (!known) {
            known = new Map();
            this.storedFolderFiles.set(folderUrl, known);
        }
        for (const [authored, uri, sha256] of hashed) {
            if (sha256 === undefined) known.delete(authored);
            else known.set(authored, { uri, sha256 });
            this.registerAssetUrl(uri, new URL(uri, folderUrl).href);
        }
    }

    /**
     * Drop what {@link recordStoredFiles} knows about `folderUrl`, or about
     * every folder, including the folder-file registrations it made: the
     * next save sends those files again, and names none of them.
     */
    public forgetStoredFiles(folderUrl?: string): void {
        for (const folder of folderUrl === undefined ? [...this.storedFolderFiles.keys()] : [folderUrl]) {
            for (const { uri } of this.storedFolderFiles.get(folder)?.values() ?? []) {
                if (this.assetUrls.get(uri) === new URL(uri, folder).href) this.assetUrls.delete(uri);
            }
            this.storedFolderFiles.delete(folder);
        }
    }

    /** Whether anything is recorded for `folderUrl`. */
    public hasStoredFiles(folderUrl: string): boolean {
        return (this.storedFolderFiles.get(folderUrl)?.size ?? 0) > 0;
    }

    /**
     * The uri `folderUrl` stored `bytes` under for `authoredUri`, when those
     * exact bytes were recorded for it.
     */
    public async storedFileFor(folderUrl: string, authoredUri: string, bytes: Blob): Promise<string | undefined> {
        const record = this.storedFolderFiles.get(folderUrl)?.get(authoredUri);
        if (!record) return undefined;
        return (await sha256Hex(bytes)) === record.sha256 ? record.uri : undefined;
    }

    /** The uri a recorded save stored `authoredUri` under in `folderUrl`. */
    public storedUriIn(folderUrl: string, authoredUri: string): string | undefined {
        return this.storedFolderFiles.get(folderUrl)?.get(authoredUri)?.uri;
    }

    /**
     * Record the ORIGINAL human name of a folder file (spec §2.1) — what the
     * user dropped, or what `POST /files` echoed back.
     *
     * Informational only. {@link getAssetName} is read by the writers so a
     * round trip preserves `files[k].name`; nothing resolves bytes by it, and
     * a missing name is never an error — the writer simply omits the field.
     */
    public registerAssetName(assetKey: string, name: string | undefined): void {
        if (!assetKey || !name) return;
        this.assetNames.set(assetKey.replace('./', ''), name);
    }

    /** The original human name recorded for an asset key, if any. */
    public getAssetName(pathOrKey: string): string | undefined {
        if (!pathOrKey) return undefined;
        return this.assetNames.get(pathOrKey.replace('./', ''));
    }

    /**
     * Register the directory URL an asset was fetched from, so relative
     * references inside it (a .gltf's `buffers[].uri`/`images[].uri`) can be
     * fetched from the same location even though the asset itself loads from
     * a local blob. `baseUrl` must end with `/`.
     */
    public registerAssetBaseUrl(pathOrKey: string, baseUrl: string): void {
        if (!baseUrl) return;
        this.assetBaseUrls.set(pathOrKey.replace('./', ''), baseUrl);
    }

    /** Original directory for companion files of a locally-held glTF asset. */
    public getAssetBaseUrl(pathOrKey: string): string | undefined {
        const key = this.urlToAssetKey.get(pathOrKey) ?? pathOrKey.replace('./', '');
        return this.assetBaseUrls.get(key);
    }

    /**
     * Canonical form of an external-reference relative path, applied to BOTH
     * sibling registration keys and resolution lookups so the two can never
     * disagree on encoding. glTF/glXF uris are percent-encoded
     * (`Wood%20Floor.png`) while on-disk filenames are not (`Wood Floor.png`)
     * — and different load paths hand us either form. Canonical = decoded,
     * forward-slashed (Windows-exporter backslashes normalized, matching
     * extractFilename's defense), no leading `./`, dot-segments collapsed
     * (a nested sub-document ref like `sub/../tex.png` must land on the
     * same key as the runtime's `tex.png`).
     */
    private static canonicalRelPath(rel: string): string {
        let decoded = rel;
        try {
            decoded = decodeURIComponent(rel);
        } catch { /* malformed escape (or a literal '%') — use raw */ }
        return ResourceManager.normalizeRelPath(decoded);
    }

    private static normalizeRelPath(path: string): string {
        const segments: string[] = [];
        for (const seg of path.replace(/\\/g, '/').split('/')) {
            if (seg === '' || seg === '.') continue;
            if (seg === '..') {
                // Collapse against a prior segment; leading '..' has nothing
                // to collapse and is kept (resolution will fall back to
                // basename probing).
                if (segments.length > 0 && segments[segments.length - 1] !== '..') {
                    segments.pop();
                    continue;
                }
            }
            segments.push(seg);
        }
        return segments.join('/');
    }

    /**
     * Register companion files that accompany a single-file open (a .gltf's
     * .bin/textures, a .glxf's referenced GLBs). Blobs are stored under
     * `assets/<relPath>` and made resolvable by their original relative path,
     * so both the glTF external-ref resolver and `getAsset` find them.
     *
     * Registration is last-writer-wins: two imports whose files ship a
     * same-named companion (e.g. exporter-default `file_0.bin`) collide on
     * the same key, so the overwrite is logged rather than silent.
     *
     * @param siblings - Map of relative path (as referenced by the opened
     *   file, e.g. `file_0.bin` or `textures/wood.png`) → blob
     */
    public registerSiblingAssets(siblings: ReadonlyMap<string, Blob>): void {
        for (const [relPath, blob] of siblings) {
            const clean = ResourceManager.canonicalRelPath(relPath);
            const key = clean.startsWith('assets/') ? clean : `assets/${clean}`;
            const existing = this.assets.get(key);
            if (existing && existing !== blob) {
                Logger.warn(`[ResourceManager] Sibling asset overwrites an existing blob: ${key} — a previously imported asset referencing it will resolve to the new content`);
            }
            this.assets.set(key, blob);
            // Resolvable by the (canonicalized) reference too (getAsset consults this).
            this.urlToAssetKey.set(clean, key);
        }
        if (siblings.size > 0) {
            Logger.log(`[ResourceManager] Registered ${siblings.size} sibling asset(s)`);
        }
    }

    /**
     * Resolve an external reference (a relative uri from a .gltf/.glxf) to a
     * registered blob. This owns the canonical probe order so every
     * resolution site — the glTF `preprocessUrlAsync` resolver and the glXF
     * plugin's external-asset lookup — matches the same key forms:
     *
     *   1. `assets/<rel>` and `<rel>` (including URL/sibling mappings)
     *   2. the same two forms by basename (multi-select flattens
     *      directories, so a `textures/wood.png` ref may be registered as
     *      `wood.png`)
     *
     * Input may be percent-encoded (verbatim document uri) or decoded (an
     * on-disk filename); both hit the same canonical key space as
     * registration (see {@link canonicalRelPath}).
     *
     * Export passes `modelPath` to anchor relative paths and flattened
     * siblings to that model's directory. Runtime callers omit it and keep
     * the historical global probe ladder above.
     */
    public resolveExternalAssetBlob(uri: string, modelPath?: string): Blob | undefined {
        const rel = ResourceManager.canonicalRelPath(uri);
        if (modelPath !== undefined) {
            if (/^[a-z][a-z\d+.-]*:/i.test(uri) || uri.startsWith('//')) return this.getAsset(uri);
            const source = (this.urlToAssetKey.get(modelPath) ?? modelPath).replace(/^\.\//, '');
            if (/^[a-z][a-z\d+.-]*:/i.test(source)) {
                return this.getAsset(new URL(uri.replace(/\\/g, '/'), source).href);
            }
            // Stored model keys are already decoded; preserve literal percent escapes in their namespace.
            const modelKey = this.assets.has(source) ? source : ResourceManager.canonicalRelPath(source);
            const directory = modelKey.slice(0, modelKey.lastIndexOf('/') + 1);
            if (uri.startsWith('/')) return this.getAsset(rel);
            const path = ResourceManager.normalizeRelPath(directory + rel);
            // A flattened multi-select can put the file beside its model, never in another package.
            const direct = path.startsWith('../') || path === '..' ? undefined : this.getAsset(path);
            return direct ?? this.getAsset(directory + rel.split('/').pop());
        }

        const direct = this.getAsset(`assets/${rel}`) ?? this.getAsset(rel);
        if (direct) return direct;
        const fileName = rel.split('/').pop();
        if (!fileName || fileName === rel) return undefined;
        const byBasename = this.getAsset(`assets/${fileName}`) ?? this.getAsset(fileName);
        if (byBasename) {
            // Basename matching is inherently ambiguous when a scene carries
            // same-named files in different subdirectories — the winner is
            // whichever registered, so surface it rather than fail silently.
            const suffix = `/${fileName}`;
            let candidates = 0;
            for (const key of this.assets.keys()) {
                if (key.endsWith(suffix)) candidates++;
            }
            if (candidates > 1) {
                Logger.warn(`[ResourceManager] Ambiguous basename fallback for "${rel}": ${candidates} registered assets end in "${fileName}" — resolved to an arbitrary one`);
            }
        }
        return byBasename;
    }

    /**
     * Build a glTF `preprocessUrlAsync` hook that resolves external references
     * of a blob-loaded glTF. Babylon computes `rootUrl` as the folder of the
     * blob URL and resolves each `buffers[].uri`/`images[].uri` as
     * `rootUrl + uri` — an address that can never exist. This hook recovers
     * the original relative uri and resolves it, in order:
     *
     *   1. a registered sibling blob (`assets/<rel>` or via URL mapping),
     *   2. the asset's registered source base URL (remote .gltf),
     *   3. pass-through (data:, absolute http(s), unknown).
     *
     * Call `dispose()` after the load completes to revoke any object URLs
     * created for sibling blobs.
     */
    public createGltfExternalRefResolver(
        assetPathOrUrl: string,
        blobUrl: string
    ): { preprocessUrlAsync: (url: string) => Promise<string>; dispose: () => void } {
        const key = this.urlToAssetKey.get(assetPathOrUrl) ?? assetPathOrUrl.replace('./', '');
        const baseUrl = this.assetBaseUrls.get(key);
        // Babylon's rootUrl for a blob URL is everything up to the last '/'.
        const prefix = blobUrl.substring(0, blobUrl.lastIndexOf('/') + 1);
        const created: string[] = [];

        const preprocessUrlAsync = async (url: string): Promise<string> => {
            if (url === blobUrl || url.startsWith('data:')) return url;
            if (!url.startsWith(prefix)) return url; // absolute/external — untouched
            const rawRel = url.substring(prefix.length);

            // 1. Local sibling blob — pass the verbatim (possibly encoded)
            // rel; resolveExternalAssetBlob owns the single decode so both
            // sides of the key space decode exactly once.
            const sibling = this.resolveExternalAssetBlob(rawRel);
            if (sibling) {
                const objectUrl = URL.createObjectURL(sibling);
                created.push(objectUrl);
                return objectUrl;
            }

            // 2. Remote source directory
            if (baseUrl) {
                return baseUrl + rawRel;
            }

            Logger.warn(`[ResourceManager] Unresolved external reference: ${rawRel} (from ${key})`);
            return url;
        };

        const dispose = () => {
            for (const objectUrl of created) URL.revokeObjectURL(objectUrl);
            created.length = 0;
        };

        return { preprocessUrlAsync, dispose };
    }

    /**
     * Strip the `assets/` folder prefix from an asset reference, in either
     * spelling the codebase produces (`./assets/foo.glb` and the bare
     * `assets/foo.glb` — see {@link isLocalAssetPath}; the loaders emit both).
     *
     * Export uses the result as the ResourceManager key and re-prepends
     * `assets/` when writing the archive, so handling only the `./` form
     * meant a bare reference (a) landed at `assets/assets/foo.glb` in the
     * archive, and (b) keyed *separately* from the same blob referenced with
     * `./` — one shared asset, two copies of its bytes in the zip.
     */
    public static removeAssetsPrefix(input: string): string {
        for (const prefix of ['./assets/', 'assets/']) {
            if (input.startsWith(prefix)) {
                return input.slice(prefix.length);
            }
        }
        return input;
    }

    /**
     * Build the zip archive and return it as a Blob.
     *
     * The archive is assembled in a Web Worker (see zipWriter) so a running
     * render loop can't starve the build. Compression follows the single
     * shared per-file policy ({@link deflateLevelForPath}): DEFLATE for
     * geometry/JSON/scripts, STORE for already-compressed media. `onProgress`
     * fires after each entry is written.
     *
     * This used to STORE *every* asset ("parity with the previous JSZip
     * default"), which is why a re-exported `.trace` came out substantially
     * larger than the same scene exported as `.tracex` — `.tracex` has always
     * run its bundled assets through the policy. GLB geometry buffers
     * compress well (measured 43MB → 32MB on a real scene), so a
     * model-heavy scene paid the whole difference and could cross an upload
     * limit on nothing but archive framing.
     */
    public async createTraceBlob(onProgress?: ZipProgressCallback): Promise<Blob> {
        if (!this.json) {
            throw new Error("Missing json");
        }

        const { createZipBlob, deflateLevelForPath } = await import('../export/zip/zipWriter');

        Logger.log("Adding assets to zip file:", this.assets.size);
        // Read all asset blobs in one parallel wave — a sequential per-asset
        // await yields to the event loop each time, and with an active render
        // loop every yield can cost a frame (~16ms × asset count).
        const entries: ZipEntry[] = await Promise.all(
            [...this.assets].map(async ([assetName, blob]) => ({
                path: `assets/${assetName}`,
                data: new Uint8Array(await blob.arrayBuffer()),
                level: deflateLevelForPath(assetName),
            })),
        );

        if (this.info) {
            entries.push({ path: "models.json", data: strToU8(JSON.stringify(this.json, null, 2)), level: 6 });
            entries.push({ path: "info.json", data: strToU8(JSON.stringify(this.info, null, 2)), level: 6 });
        } else {
            entries.push({ path: "scene.json", data: strToU8(JSON.stringify(this.json, null, 2)), level: 6 });
        }

        const archive = await createZipBlob(entries, onProgress);

        // Size reports ("the re-export got bigger") are otherwise unfalsifiable
        // without the artifact — one line naming the entry count and the
        // raw→archive ratio turns the next one into a lookup.
        const rawBytes = entries.reduce((sum, e) => sum + e.data.length, 0);
        Logger.log(
            `[ResourceManager] Archive: ${entries.length} entries, `
            + `${rawBytes} bytes raw → ${archive.size} bytes packed `
            + `(${rawBytes > 0 ? Math.round((archive.size / rawBytes) * 100) : 100}%)`,
        );

        return archive;
    }

    /**
     * Load assets from a zip file.
     * @param zipBlob - The zip file as a Blob.
     */
    async loadAssetsFromZip(zipBlob: Blob): Promise<void> {
        try {
            const zip = await JSZip.loadAsync(zipBlob);
            const promises: Promise<void>[] = [];

            zip.forEach((relativePath, zipEntry) => {
                // Support both 'assets/' and 'Assets/' folders (Aero files use uppercase)
                const lowerPath = relativePath.toLowerCase();
                if (lowerPath.startsWith('assets/')) {
                    promises.push(
                        zipEntry.async('blob').then(blob => {
                            // Normalize 'Assets/' to 'assets/' but preserve filename case
                            const normalizedPath = relativePath.replace(/^Assets\//i, 'assets/');
                            this.assets.set(normalizedPath, blob);
                        })
                    );
                }
            });

            await Promise.all(promises);
            Logger.log("All assets loaded successfully");
        } catch (error) {
            Logger.error("Error loading assets from zip:", error);
        }
    }

    /**
     * Check if a path is local.
     * @param path - The path to check.
     * @returns `true` if the path is local, otherwise `false`.
     */
    isLocal(path: string): boolean {
        // Anything leading with `/` is fetched rather than looked up as a
        // registered blob: site-absolute paths (`/generated/…`, `/components/…`)
        // are served by the origin, and protocol-relative URLs (`//host/…`)
        // carry their own host. Deliberately broader than `isSiteAbsolutePath`,
        // which excludes `//` — here both are non-local, and the old
        // `!startsWith("http")` test misclassified `//host/…` as local.
        // (Registered/packaged assets use `assets/…` or `./assets/…` keys.)
        if (path.startsWith("/")) return false;
        return !path.startsWith("http");
    }

    /**
     * Retrieves an asset blob by a path formatted like ./assets/assetname.extension.
     * Also resolves external URLs via the URL→asset-key mapping.
     * @param path - The path to the asset (local or HTTP URL).
     * @returns The Blob of the asset or undefined if not found.
     */
    getAsset(path: string): Blob | undefined {
        // Remove './' prefix (assets are stored as 'assets/filename')
        const blob = this.assets.get(path.replace('./', ''));
        if (blob) return blob;

        // Check URL→asset-key mapping for HTTP-sourced assets
        const localKey = this.urlToAssetKey.get(path);
        return localKey ? this.assets.get(localKey) : undefined;
    }

    /**
     * Create a URL for an asset.
     * @param path - The path to the asset.
     * @returns The object URL for the asset or `null` if not found.
     */
    createAssetURL(path: string): string | null {
        const assetBlob = this.getAsset(path);
        if (assetBlob) {
            return URL.createObjectURL(assetBlob);
        } else {
            Logger.error(`Asset not found for path: ${path}`);
            return null;
        }
    }

    /**
     * Canonical container-cache key for a path. Local paths follow the
     * URL/sibling mapping so one blob addressable by two forms (`foo.glb`
     * via a sibling mapping and `assets/foo.glb` directly) can't be parsed
     * into two independently-refcounted containers. Remote URLs keep their
     * verbatim key (their local-blob indirection is resolved inside
     * _loadContainer, and re-keying them could split get/release pairs
     * around a late registerUrlMapping).
     */
    private containerKey(path: string): string {
        const clean = path.replace('./', '');
        if (!this.isLocal(clean)) return clean;
        return this.urlToAssetKey.get(clean) ?? clean;
    }

    /** Cache/refcount key: the canonical path key plus a suffix per load option. */
    private containerCacheKey(key: string, opts?: ContainerLoadOptions): string {
        return opts?.allMaterials ? key + ALL_MATERIALS_KEY_SUFFIX : key;
    }

    /**
     * Get or load a model into a cached AssetContainer.
     * First call for a given path triggers LoadAssetContainerAsync.
     * Subsequent calls return the same cached promise (no re-parsing/re-fetching).
     * Works for both local blob assets and remote URLs.
     * Callers must use container.instantiateModelsToScene() to create instances.
     * Call releaseContainer() when the instance is no longer needed.
     *
     * A REJECTED load evicts itself from the cache: no ref survives the
     * rejection, a later call for the same path re-fetches, and callers must
     * not releaseContainer() on rejection (doing so is a harmless no-op).
     *
     * `opts` selects a variant of the load (see {@link ContainerLoadOptions});
     * release with the same `opts`.
     */
    public getOrLoadContainer(path: string, scene: Scene, pluginExtension?: string, opts?: ContainerLoadOptions): Promise<AssetContainer> {
        const pathKey = this.containerKey(path);
        const key = this.containerCacheKey(pathKey, opts);

        const existing = this.containerCache.get(key);
        if (existing) {
            this.containerRefCount.set(key, (this.containerRefCount.get(key) || 0) + 1);
            Logger.debug(`[ResourceManager] Container cache hit: ${key}`);
            return existing;
        }

        // `_loadContainer` bottoms out in Babylon's `LoadAssetContainerAsync`,
        // which can leave a dropped address unanswered for good (see
        // `withDownloadStall`), and every caller (Model, every Renderer
        // material slot) would wait on it forever. Bounding it here protects
        // all of them at once.
        //
        // Only a stalled DOWNLOAD fails: bytes known to be missing and none
        // for `DOWNLOAD_STALL_MS`. A 6 MB model on a slow link takes half a
        // minute and is still arriving the whole time; a model whose decode
        // takes longer than that on a slow phone is still delivered.
        const loadPromise = withDownloadStall(
            progress => this._loadContainer(pathKey, path, scene, pluginExtension, opts, progress),
            () => `[ResourceManager] Container "${path}"`,
            late => late.dispose(),
            this.downloads,
        );
        this.containerCache.set(key, loadPromise);
        this.containerRefCount.set(key, 1);
        loadPromise.then(container => this.settledContainers.set(loadPromise, container), () => {});

        // A rejected load must not poison the cache: without eviction, the
        // rejected promise is served from cache forever (a transient 404/CORS
        // blip becomes permanent) and every retry's cache-hit increments a
        // refcount nobody can meaningfully release. The identity guard keeps a
        // late rejection from evicting a FRESH entry that reclaimed the key
        // after this one was released mid-flight.
        loadPromise.catch(() => {
            if (this.containerCache.get(key) === loadPromise) {
                this.containerCache.delete(key);
                this.containerRefCount.delete(key);
                Logger.debug(`[ResourceManager] Evicted failed container load: ${key}`);
            }
        });

        return loadPromise;
    }

    /**
     * The cached container for `path` if its load has already finished, read
     * synchronously; undefined while it is still loading or not cached. Takes
     * no reference: a caller that keeps the container acquires it with
     * {@link getOrLoadContainer}, which is then a cache hit.
     *
     * Exists for work that cannot yield, e.g. a state switch inside a
     * thumbnail's synchronous pose window, where an `await` on even a settled
     * promise lands after the render.
     */
    public peekLoadedContainer(path: string, opts?: ContainerLoadOptions): AssetContainer | undefined {
        const promise = this.containerCache.get(this.containerCacheKey(this.containerKey(path), opts));
        return promise ? this.settledContainers.get(promise) : undefined;
    }

    private async _loadContainer(key: string, originalPath: string, scene: Scene, pluginExtension: string | undefined, opts: ContainerLoadOptions | undefined, progress?: DownloadProgress): Promise<AssetContainer> {
        const ext = pluginExtension ?? `.${Utility.getFileExtensionFromSrc(key) || 'glb'}`;
        const gltf = this._gltfPluginOptions(opts);

        // Remote URL: check if we have a cached local blob first (e.g. catalog
        // import). getAsset (not a raw urlToAssetKey lookup) so a blob
        // registered directly under its URL — `addAsset('/generated/x.glb', …)`,
        // which is how generated and origin-served assets enter the store —
        // is served from cache instead of re-fetched from the origin.
        if (!this.isLocal(originalPath)) {
            const localBlob = this.getAsset(originalPath);
            if (localBlob) {
                const container = await this._loadContainerFromBlob(localBlob, originalPath, ext, scene, gltf, progress);
                Logger.debug(`[ResourceManager] Loaded container from cache for URL: ${originalPath}`);
                return container;
            }

            const container = await LoadAssetContainerAsync(originalPath, scene, {
                pluginExtension: ext,
                ...(gltf ? { pluginOptions: { gltf } } : {}),
                ...progressOption(progress),
            });
            Logger.debug(`[ResourceManager] Loaded remote container: ${key}`);
            return container;
        }

        // Local blob asset: create temporary blob URL. getAsset (not a raw map
        // lookup) so keys registered via URL/sibling mappings resolve too.
        const blob = this.getAsset(key);
        if (!blob) {
            // Nothing local: a folder document's relative reference resolves
            // against the document's own URL (spec §2.3).
            const folderUrl = this.resolveAssetUrl(originalPath) ?? this.resolveAssetUrl(key);
            if (folderUrl) {
                const container = await LoadAssetContainerAsync(folderUrl, scene, {
                    pluginExtension: ext,
                    ...(gltf ? { pluginOptions: { gltf } } : {}),
                    ...progressOption(progress),
                });
                Logger.debug(`[ResourceManager] Loaded folder container: ${key} → ${folderUrl}`);
                return container;
            }
            throw new Error(`[ResourceManager] Asset not found: ${key}`);
        }

        const container = await this._loadContainerFromBlob(blob, key, ext, scene, gltf, progress);
        Logger.debug(`[ResourceManager] Loaded container: ${key}`);
        return container;
    }

    /** glTF plugin options implied by the load options; undefined when there are none. */
    private _gltfPluginOptions(opts: ContainerLoadOptions | undefined): GltfPluginOptions {
        return {
            // A cached container is a template, never shown: its clips must
            // not run. Babylon's default starts the first one on load, and a
            // started group keeps animating the container's own nodes — and,
            // through an instance that shares its MorphTargetManager, every
            // placement of the file. Playback belongs to the instantiated
            // clips, driven by the runtime (RECORDINGS_SPEC §7.1).
            animationStartMode: GLTFLoaderAnimationStartMode.NONE,
            ...(opts?.allMaterials ? { loadAllMaterials: true } : {}),
        };
    }

    /**
     * Load an AssetContainer from a local blob via a temporary object URL.
     * For glTF content, installs the external-ref resolver so a text .gltf's
     * `.bin`/texture references resolve to sibling blobs or its source URL.
     */
    private async _loadContainerFromBlob(blob: Blob, assetPathOrUrl: string, ext: string, scene: Scene, gltfOptions?: GltfPluginOptions, progress?: DownloadProgress): Promise<AssetContainer> {
        const blobUrl = URL.createObjectURL(blob);
        const isGltf = ext === '.gltf' || ext === '.glb';
        const resolver = isGltf ? this.createGltfExternalRefResolver(assetPathOrUrl, blobUrl) : undefined;
        const gltf: GltfPluginOptions | undefined = resolver || gltfOptions
            ? { ...gltfOptions, ...(resolver ? { preprocessUrlAsync: resolver.preprocessUrlAsync } : {}) }
            : undefined;
        try {
            return await LoadAssetContainerAsync(blobUrl, scene, {
                pluginExtension: ext,
                ...(gltf ? { pluginOptions: { gltf } } : {}),
                ...progressOption(progress),
            });
        } finally {
            URL.revokeObjectURL(blobUrl);
            resolver?.dispose();
        }
    }

    /**
     * Release a reference to a cached container.
     * When ref count reaches 0, the container is disposed and removed from cache.
     */
    public releaseContainer(path: string, opts?: ContainerLoadOptions): void {
        const key = this.containerCacheKey(this.containerKey(path), opts);
        const count = this.containerRefCount.get(key);
        if (count === undefined) return;

        if (count <= 1) {
            this.containerRefCount.delete(key);
            const promise = this.containerCache.get(key);
            this.containerCache.delete(key);
            promise?.then(c => c.dispose()).catch(() => {});
            Logger.debug(`[ResourceManager] Disposed container: ${key}`);
        } else {
            this.containerRefCount.set(key, count - 1);
        }
    }

    /** Return a snapshot of all stored assets as [path, blob] pairs. */
    public getAssetEntries(): [string, Blob][] {
        return [...this.assets.entries()];
    }

    /** Number of stored assets. */
    public get assetCount(): number {
        return this.assets.size;
    }

    /** Remove a single asset by path. Returns true if the asset existed. */
    public removeAsset(path: string): boolean {
        const blob = this.assets.get(path);
        if (!blob) return false;

        this.assets.delete(path);
        this.assetBaseUrls.delete(path.replace('./', ''));
        this.assetUrls.delete(path.replace('./', ''));
        this.assetSourceUrls.delete(path.replace('./', ''));
        this.assetNames.delete(path.replace('./', ''));

        // Remove from URL mapping
        for (const [url, assetKey] of this.urlToAssetKey) {
            if (assetKey === path) {
                this.urlToAssetKey.delete(url);
                break;
            }
        }

        // Release cached container if present
        this._evictContainer(path);

        Logger.debug(`[ResourceManager] Removed asset: ${path}`);
        return true;
    }

    /**
     * Clear all assets (useful for cleanup).
     */
    clearAssets(): void {
        this.assets.clear();
        this.sourceToSceneFolder.clear();
        this.urlToAssetKey.clear();
        this.usedSceneFolders.clear();
        this.assetBaseUrls.clear();
        this.assetUrls.clear();
        this.assetSourceUrls.clear();
        this.assetNames.clear();

        // Dispose all cached containers, plus the ones a re-upload replaced
        // while they were still held.
        for (const promise of [...this.containerCache.values(), ...this._orphaned]) {
            promise.then(c => c.dispose()).catch(() => {});
        }
        this.containerCache.clear();
        this.containerRefCount.clear();
        this._orphaned.length = 0;

        Logger.log("All assets cleared");
    }
}

/**
 * Hex SHA-256 of every byte of `blob`, or undefined without SubtleCrypto
 * (an insecure context).
 */
async function sha256Hex(blob: Blob): Promise<string | undefined> {
    if (!globalThis.crypto?.subtle) return undefined;
    const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
    return Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('');
}
